/**
 * Admin backend API client.
 *
 * 所有端点要求 cookie 里的 jwt 对应 ADMIN_OPEN_ID，否则后端返回 403。
 */

import { apiGet, apiSend } from "./client";

export type Counters = {
  favorites: number;
  shared_to_me: number;
  shared_by_me: number;
  groups: number;
  forwards: number;
};

export type AdminUser = {
  id: number;
  open_id: string;
  name: string | null;
  avatar_url: string | null;
  created_at: string;
  refresh_expires_at: string;
  favorites_count: number;
};

export type AdminUserDetail = {
  open_id: string;
  union_id: string | null;
  name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  access_expires_at: string;
  refresh_expires_at: string;
  refresh_revoked: boolean;
  counters: Counters;
};

export type Page<T> = { items: T[]; next_cursor: number | null };

export type AdminFavorite = {
  id: number;
  message_id: string;
  msg_type: string;
  content_text: string;
  sender_name: string | null;
  source_chat_id: string | null;
  pinned_at: string | null;
  created_at: string;
};

export type AdminShare = {
  id: number;
  favorite_id: number;
  shared_by: string;
  shared_to: string;
  via_group_id: number | null;
  notify_message_id: string | null;
  pinned_at: string | null;
  created_at: string;
};

export type AdminGroup = {
  id: number;
  name: string;
  created_at: string;
  members: { open_id: string; name: string | null }[];
};

export type AdminActivity = {
  id: number;
  favorite_id: number | null;
  target_type: string;
  target_id: string;
  feishu_msg_id: string | null;
  status: string;
  error: string | null;
  created_at: string;
};

export type AuditItem = {
  source: "admin" | "forward";
  id: number;
  ts: string;
  actor: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  status: string | null;
  reason: string | null;
  error: string | null;
  payload: unknown;
};

export type AuditPage = {
  items: AuditItem[];
  next_cursor: { ts: string | null; id: number } | null;
};

export type Health = {
  db_ok: boolean;
  db_error: string | null;
  ws_connected: boolean;
  pool: {
    size: number | null;
    checked_in: number | null;
    checked_out: number | null;
    overflow: number | null;
  };
  events_last_1h: Record<string, number>;
  feishu_calls_last_1h: Record<string, { ok?: number; err?: number }>;
  feishu_calls_total_1h: number;
  feishu_error_rate_1h: number;
  uptime_seconds: number;
};

export type Stats = {
  totals: { users: number; favorites: number; shares: number; groups: number };
  msg_type_distribution: Record<string, number>;
  daily_favorites_last_7d: { day: string; count: number }[];
};

function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") sp.append(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const adminApi = {
  listUsers: (q: string, cursor: number | null, limit = 20) =>
    apiGet<Page<AdminUser>>(
      `/api/admin/users${qs({ q, cursor_id: cursor ?? undefined, limit })}`,
    ),

  userDetail: (openId: string) =>
    apiGet<AdminUserDetail>(`/api/admin/users/${encodeURIComponent(openId)}`),

  userFavorites: (openId: string, cursor: number | null) =>
    apiGet<Page<AdminFavorite>>(
      `/api/admin/users/${encodeURIComponent(openId)}/favorites${qs({
        cursor_id: cursor ?? undefined,
      })}`,
    ),

  userShares: (openId: string, direction: "to" | "by", cursor: number | null) =>
    apiGet<Page<AdminShare>>(
      `/api/admin/users/${encodeURIComponent(openId)}/shares${qs({
        direction,
        cursor_id: cursor ?? undefined,
      })}`,
    ),

  userGroups: (openId: string) =>
    apiGet<{ items: AdminGroup[] }>(
      `/api/admin/users/${encodeURIComponent(openId)}/groups`,
    ),

  userActivity: (openId: string, cursor: number | null) =>
    apiGet<Page<AdminActivity>>(
      `/api/admin/users/${encodeURIComponent(openId)}/activity${qs({
        cursor_id: cursor ?? undefined,
      })}`,
    ),

  revokeToken: (openId: string, reason: string | null) =>
    apiSend<void>(
      `/api/admin/users/${encodeURIComponent(openId)}/revoke_token`,
      "POST",
      { reason },
    ),

  forceDeleteFavorite: (favId: number, reason: string | null) =>
    apiSend<void>(
      `/api/admin/favorites/${favId}${qs({ reason: reason ?? undefined })}`,
      "DELETE",
    ),

  forceUnshare: (shareId: number, reason: string | null) =>
    apiSend<void>(
      `/api/admin/shares/${shareId}${qs({ reason: reason ?? undefined })}`,
      "DELETE",
    ),

  audit: (params: {
    actor?: string;
    action_type?: string;
    cursor_ts?: string | null;
    cursor_id?: number | null;
  }) =>
    apiGet<AuditPage>(
      `/api/admin/audit${qs({
        actor: params.actor,
        action_type: params.action_type,
        cursor_ts: params.cursor_ts ?? undefined,
        cursor_id: params.cursor_id ?? undefined,
      })}`,
    ),

  health: () => apiGet<Health>(`/api/admin/health`),
  stats: () => apiGet<Stats>(`/api/admin/stats`),
};
